import { store, getContext, getElement } from '@wordpress/interactivity';

// ── Helpers ────────────────────────────────────────────────────────

function hasAnalyticsConsent() {
	if ( ! window.__ndgBlockEmbeds ) return true;
	try {
		const raw = localStorage.getItem( 'ndg_consent' );
		return raw ? !!JSON.parse( raw ).analytics : false;
	} catch { return false; }
}

// Le widget Weezevent (iframe cross-origin) n'a pas accès à notre
// window.__ndgConsent — on fait donc passer le choix de consentement pub
// via un paramètre d'URL, lu côté GTM Weezevent par une variable native
// (pas de JS custom, non autorisé dans leur conteneur — voir doc Weezevent).
function hasAdsConsent() {
	try {
		const raw = localStorage.getItem( 'ndg_consent' );
		return raw ? !!JSON.parse( raw ).ads : false;
	} catch { return false; }
}

function buildEmbed( rawUrl ) {
	const yt = rawUrl.match( /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/ );
	if ( yt ) return `<iframe src="https://www.youtube.com/embed/${ yt[1] }?autoplay=1&rel=0&controls=0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;

	const vm = rawUrl.match( /vimeo\.com\/(\d+)/ );
	if ( vm ) return `<iframe src="https://player.vimeo.com/video/${ vm[1] }?autoplay=1" allow="autoplay; fullscreen" allowfullscreen></iframe>`;

	return `<video src="${ rawUrl }" autoplay controls playsinline></video>`;
}

function isThirdPartyUrl( url ) {
	return /youtube|youtu\.be|vimeo|dailymotion/i.test( url );
}

function consentPlaceholder() {
	return `<div class="ndg-consent-gate" style="aspect-ratio:16/9;display:flex;align-items:center;justify-content:center;text-align:center;padding:24px">
		<div>
			<p class="ndg-consent-gate__text">Ce contenu est hébergé par un service tiers.<br>Acceptez les cookies pour l'afficher.</p>
			<button class="ndg-consent__btn ndg-consent__btn--accept ndg-consent-gate__btn" type="button">Accepter et afficher</button>
		</div>
	</div>`;
}

function getSidebarHost() {
	return document.querySelector( '#ndg-modal-sidebar [data-wp-ref="content"]' );
}

function embedOrConsent( src ) {
	if ( isThirdPartyUrl( src ) && ! hasAnalyticsConsent() ) return consentPlaceholder();
	return buildEmbed( src );
}

// ── Inject sidebar content ─────────────────────────────────────────
// Each content type has its own small injector.
// The pool DOM pattern (templateId) is the default — same as quote-modal.

function injectPoolContent( templateId ) {
	const host    = getSidebarHost();
	const content = document.getElementById( templateId )?.firstElementChild;
	if ( content && host ) {
		host.innerHTML = '';
		host.appendChild( content );
	}
}

function injectWeezevent( weezId ) {
	const host = getSidebarHost();
	if ( ! host ) return;
	const lang        = ( document.documentElement.lang || 'fr' ).slice( 0, 2 );
	const consentParam = hasAdsConsent() ? '&ndg_ads_consent=1' : '';
	host.innerHTML = `<a class="weezevent-widget-integration"
		href="https://weezevent.com/?c=sys_widget"
		data-src="https://widget.weezevent.com/ticket/E${ weezId }/?locale=${ lang }&width_auto=1&color_primary=000000${ consentParam }"
		data-width="650" data-height="600"
		data-id="${ weezId }"
		data-resize="1" data-width_auto="1" data-noscroll="0"
		data-use-container="yes" data-type="neo"
		target="_blank" rel="noopener">Billetterie Weezevent</a>`;
	const s = document.createElement( 'script' );
	s.src = 'https://widget.weezevent.com/weez.js';
	host.appendChild( s );
}

function injectIframe( src ) {
	const host = getSidebarHost();
	if ( ! host ) return;
	if ( isThirdPartyUrl( src ) && ! hasAnalyticsConsent() ) {
		host.innerHTML = consentPlaceholder();
	} else {
		host.innerHTML = `<iframe class="ndg-modal__iframe" src="${ src }" loading="lazy" allowfullscreen title=""></iframe>`;
	}
}

// ── Store ──────────────────────────────────────────────────────────

const { state, actions } = store( 'ndg/modal', {
	state: {
		isOpen:     false,
		variant:    'sidebar',
		templateId: null,
		get isSidebarOpen() { return state.isOpen && state.variant === 'sidebar'; },
		get isCenterOpen()  { return state.isOpen && state.variant === 'center'; },
	},

	actions: {
		open( event ) {
			event?.preventDefault();
			event?.stopPropagation();

			const ctx     = getContext() || {};
			const { ref } = getElement();
			const variant = ctx.variant || 'sidebar';

			// Bouton "Réserver" d'une ligne event sans billetterie Weezevent :
			// le lien de billetterie externe s'ouvre dans un nouvel onglet,
			// même règle que la fiche event (event-single/render.php).
			const bookingUrl = ref?.dataset?.bookingUrl || '';
			// weezevent_id vaut 0 sans billetterie Weezevent : l'attribut lié
			// contient alors la chaîne "0", d'où la conversion en nombre.
			if ( bookingUrl && ! Number( ctx.weezId || ref?.dataset?.weezId ) ) {
				window.open( bookingUrl, '_blank', 'noopener' );
				return;
			}

			state.variant    = variant;
			state.templateId = ctx.templateId || null;

			if ( variant === 'center' ) {
				const host = document.querySelector( '#ndg-modal-center .ndg-modal__media' );
				const src  = ctx.iframeSrc || '';
				if ( host && src ) host.innerHTML = embedOrConsent( src );

			} else if ( state.templateId ) {
				injectPoolContent( state.templateId );

			} else {
				const weezId = ctx.weezId || ref?.dataset?.weezId || '';
				if ( weezId ) {
					injectWeezevent( weezId );
				} else {
					const src = ctx.iframeSrc || ref?.dataset?.iframeSrc || '';
					if ( src ) injectIframe( src );
				}
			}

			state.isOpen = true;
			document.body.style.overflow = 'hidden';

			requestAnimationFrame( () => {
				const id = variant === 'center' ? 'ndg-modal-center' : 'ndg-modal-sidebar';
				document.getElementById( id )?.querySelector( '.ndg-modal__close' )?.focus();
			} );
		},

		close() {
			if ( state.variant === 'sidebar' && state.templateId ) {
				const pool    = document.getElementById( state.templateId );
				const host    = getSidebarHost();
				const content = host?.firstElementChild;
				if ( content && pool ) pool.appendChild( content );
			}

			state.isOpen = false;
			document.body.style.overflow = '';

			setTimeout( () => {
				if ( state.variant === 'center' ) {
					const host = document.querySelector( '#ndg-modal-center .ndg-modal__media' );
					if ( host ) host.innerHTML = '';
				} else if ( ! state.templateId ) {
					const host = getSidebarHost();
					if ( host ) host.innerHTML = '';
				}
				state.templateId = null;
			}, 300 );
		},

		handleKeydown( event ) {
			if ( event?.key === 'Escape' && state.isOpen ) actions.close();
		},
	},
} );
